import { z } from 'zod';

import { dateOrder, emptyOr, fqdn, ipv4, numericId, requiredSelection, requiredString } from '../../utils/validation';

import { FIELD_LABELS } from './lab-constants';

const ON_DEMAND_ID_MAX = 999999999;

const onDemandId = (label) =>
    emptyOr(
        numericId(label).refine(
            (value) => Number(value) <= ON_DEMAND_ID_MAX,
            `${label} must be between 0 and ${ON_DEMAND_ID_MAX}`,
        ),
    );

const addFieldIssue = (ctx, field, rule, value) => {
    const result = rule.safeParse(value);

    if (!result.success) {
        ctx.addIssue({ code: 'custom', path: [field], message: result.error.issues[0].message });
    }
};

/**
 * Validation for the lab add/edit form. Always-visible fields are validated
 * in the base object; VPN-section fields are validated in the superRefine
 * ONLY when the form renders them (type === 'VPN', and for `parent` also
 * include_simulcast === 1) so a stored invalid value on a hidden field can
 * never block a submit with an error the user cannot see. Fields not listed
 * (free text, checkboxes, file contents) are intentionally unvalidated —
 * the backend remains authoritative.
 */
export const labEditSchema = z
    .object({
        name: requiredString(FIELD_LABELS.name),
        status: requiredSelection(FIELD_LABELS.status),
        product_id: requiredSelection(FIELD_LABELS.product_id),
        type: requiredSelection(FIELD_LABELS.type),
        on_demand_training_id_start: onDemandId(FIELD_LABELS.on_demand_training_id_start),
        on_demand_training_id_end: onDemandId(FIELD_LABELS.on_demand_training_id_end),
        parent: z.any().optional(),
        default_fqdn: z.any().optional(),
        ip_address: z.any().optional(),
        gateway_ip: z.any().optional(),
        include_simulcast: z.any().optional(),
        live_event_start: z.any().optional(),
        live_event_end: z.any().optional(),
    })
    .superRefine(
        dateOrder(
            'live_event_start',
            'live_event_end',
            `${FIELD_LABELS.live_event_end} must not be before ${FIELD_LABELS.live_event_start}`,
        ),
    )
    .superRefine((data, ctx) => {
        if (data.type !== 'VPN') {
            return;
        }

        if (data.ip_address) {
            addFieldIssue(
                ctx,
                'ip_address',
                ipv4(`${FIELD_LABELS.ip_address} must be a valid IPv4 address`),
                data.ip_address,
            );
        } else {
            ctx.addIssue({
                code: 'custom',
                path: ['ip_address'],
                message: `${FIELD_LABELS.ip_address} is required for VPN labs`,
            });
        }

        addFieldIssue(
            ctx,
            'gateway_ip',
            emptyOr(ipv4(`${FIELD_LABELS.gateway_ip} must be a valid IPv4 address`)),
            data.gateway_ip,
        );
        addFieldIssue(
            ctx,
            'default_fqdn',
            emptyOr(fqdn(`${FIELD_LABELS.default_fqdn} must be a valid hostname`)),
            data.default_fqdn,
        );

        if (data.include_simulcast === 1) {
            addFieldIssue(ctx, 'parent', emptyOr(numericId(FIELD_LABELS.parent)), data.parent);
        }
    });
